/* eslint-disable max-statements */
import { Kitchen } from '../kitchen.class';
import { KitchenEstimate, KitchenEstimateLineRow } from '../types/kitchen-estimate.type';
import { PriceBookResolutionError } from '../errors';

const formatPrice = (value: number): string =>
	new Intl.NumberFormat('en-US', { currency: 'USD', style: 'currency', maximumFractionDigits: 2 }).format(value);

const round2 = (value: number): number => Math.round(value * 100) / 100;

/**
 * Builds the ONE canonical pre-tax estimate rollup shared by every consumer (in-tool presentation
 * payload, API live presentation pricing). Category totals, promotion math, the invariant guards,
 * and the assembly figure are decided here once; callers only add tax and reshape.
 * @param kitchen Kitchen to build the estimate for
 * @returns pre-tax rollup: category totals, shipping, promotions, subtotals, line rows, promo breakdown, assembly
 * @throws when the line rows fail to reconcile to component totals, or an island/countertop row prices at $0
 * @throws PriceBookResolutionError when any row could not be resolved against the pinned price book version
 */
export const buildKitchenEstimateCfn = (kitchen: Kitchen): KitchenEstimate => {
	const { itemizedMsrp, estimateLineItems, design } = kitchen;
	const { isFreeShipping, shippingAmount, subtotal } = itemizedMsrp;

	const islands = itemizedMsrp.islands.reduce((sum, li) => sum + li.total, 0);
	const countertops = itemizedMsrp.countertops.reduce((sum, li) => sum + li.total, 0);
	const appliances = itemizedMsrp.items.reduce((sum, li) => sum + li.total, 0);
	const accessories = itemizedMsrp.accessories.reduce((sum, li) => sum + li.total, 0);
	const backsplashes = itemizedMsrp.backsplashes.reduce((sum, li) => sum + li.total, 0);
	const shipping = isFreeShipping ? 0 : shippingAmount;
	const rawSum = islands + countertops + appliances + accessories + backsplashes;
	// The +SHIPPING charge and -FREE-SHIPPING credit are both shown as line items (matching the app), but the waived amount also counts toward the total promotion savings
	const freeShippingSavings = isFreeShipping ? shippingAmount : 0;
	const promotions = round2(subtotal - rawSum - shipping - freeShippingSavings);

	// Rows come from the ONE canonical builder shared with the panel and PDF — never re-derive them here
	const lineItems: Array<KitchenEstimateLineRow> = estimateLineItems.map((row) => ({
		sku: row.sku,
		description: row.label,
		category: row.category,
		qty: row.qty,
		each: row.each,
		total: row.total,
		...(row.kind === 'promo' ? { kind: 'promo' as const } : {}),
		priceText: formatPrice(row.price),
		...(row.priceNote ? { priceNote: row.priceNote } : {})
	}));

	const promoBreakdown = lineItems
		.filter((li) => li.kind === 'promo')
		.map((li) => ({ label: li.description, amount: li.total }));

	// Reconciliation invariant: a silently dropped category fails generation loudly instead of shipping a quote that disagrees with the in-tool estimate
	const chiseledTotal = itemizedMsrp.countertops
		.filter((item) => item.sku.startsWith('RTA-Chiseled'))
		.reduce((sum, li) => sum + li.total, 0);
	// Unresolved rows contribute to no subtotal, so excluding them here keeps the mismatch throw from masking the real cause
	const productRowsTotal = estimateLineItems
		.filter((row) => row.kind !== 'promo' && !row.unresolved)
		.reduce((sum, row) => sum + row.total, 0);
	if (Math.abs(productRowsTotal + chiseledTotal - rawSum) > 0.01) {
		throw new Error(`estimate line items (${formatPrice(productRowsTotal + chiseledTotal)}) do not reconcile to component totals (${formatPrice(rawSum)})`);
	}

	// A $0 island or countertop is always a pricing-data gap (e.g. a material with no SKU prices) — never quote it
	const unpricedRows = estimateLineItems
		.filter((row) => (row.group === 'island' || row.group === 'countertop') && row.total === 0)
		.map((row) => row.sku);
	if (unpricedRows.length > 0) {
		const skus = [...new Set(unpricedRows)];
		// A `##` in the sku is the surround map's placeholder code (surround-asset.map.json), not a
		// missing price: the design sits on a legacy finish that never got a SKU code, so re-picking
		// the finish is the fix. Saying so turns a dead-end sku into an instruction.
		const legacyFinish = skus.some((sku) => sku.includes('##'));
		throw new Error(`no pricing data for: ${skus.join(', ')}${legacyFinish ? ' — this design uses a legacy surround finish with no SKU code; re-select the finish to price it' : ''}`);
	}

	const unresolvedSkus = [...new Set(estimateLineItems.filter((row) => row.unresolved).map((row) => row.sku))];
	if (unresolvedSkus.length > 0) {
		const { priceBook } = kitchen.context;
		throw new PriceBookResolutionError({
			kind: 'unresolved-records',
			versionId: priceBook?.versionId ?? null,
			versionName: priceBook?.versionName ?? null,
			unresolvedSkus,
			message: `this price book version has no record for: ${unresolvedSkus.join(', ')}`
		});
	}

	// Assembly estimate -- mirrors EstimateAssemblyCost: $250/island + $150/linear-foot
	const islandChildren = design.islands.orderedChildren;
	const feet = islandChildren.reduce((sum, island) => sum + island.linearFeet, 0);

	return {
		islands,
		countertops,
		appliances,
		accessories,
		backsplashes,
		shipping,
		promotions,
		subtotal,
		// NOT itemizedMsrp.subtotalWithoutDiscount — that already has island-scoped promotions taken off, which double-counts them
		subtotalBeforeDiscounts: round2(subtotal - promotions),
		lineItems,
		promoBreakdown,
		assembly: {
			feet,
			cost: (islandChildren.length * 250) + (feet * 150),
			skus: itemizedMsrp.islands.map((island) => `${island.sku} (x${island.qty})`)
		}
	};
};
